import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryBuilder, useSimpleQueryBuilder } from '../../../../../hooks/QueryBuilder';
import type { QueryBuilderMode } from '../../../../Clients/QueryBuilder/components/CreateQueryTab';

export const useTargetingBuilder = (queryDescription: string) => {
  const advanced = useQueryBuilder();
  const simple = useSimpleQueryBuilder();
  const [mode, setMode] = useState<QueryBuilderMode>('simple');
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [descriptionKey, setDescriptionKey] = useState(0);
  const descriptionRef = useRef(queryDescription);

  useEffect(() => {
    descriptionRef.current = queryDescription;
  }, [queryDescription]);

  const groups = mode === 'simple' ? simple.groups : advanced.groups;
  const { resetGroups } = advanced;
  const { resetFields } = simple;

  const selectGroup = useCallback((groupId: number | null, groupName?: string) => {
    setSelectedGroupId(groupId);
    if (groupName) setNewGroupName('');
  }, []);

  const createGroup = useCallback((groupName: string) => {
    setNewGroupName(groupName);
    setSelectedGroupId(-1);
  }, []);

  const reset = useCallback(() => {
    resetGroups();
    resetFields();
    descriptionRef.current = '';
    setDescriptionKey(k => k + 1);
    setSelectedGroupId(null);
  }, [resetGroups, resetFields]);

  return {
    mode,
    setMode,
    advanced,
    simple,
    groups,
    isValid: mode === 'simple' ? simple.isValid : advanced.isValid,
    hasRules: groups.some(g => g.rules.length > 0),
    descriptionRef,
    descriptionKey,
    selectedGroupId,
    newGroupName,
    selectGroup,
    createGroup,
    reset,
  };
};

export type TargetingBuilder = ReturnType<typeof useTargetingBuilder>;
